/**
 * Quran Radio - Main Application
 * Orchestrates all modules: API Client, RadioPlayer, Visualizer
 */

import { ApiClient } from './api'
import { RadioPlayer } from './radio'
import { AudioVisualizer } from './visualizer'
import type { Channel, StatusState } from './types'

// ==========================================
// Application State
// ==========================================
interface AppState {
  channels: Channel[]
  filteredChannels: Channel[]
  searchQuery: string
  channelsRendered: boolean
  currentChannel: Channel | null
  currentChannelId: string | null
  isPlaying: boolean
  isLoading: boolean
  apiClient: ApiClient | null
  radioPlayer: RadioPlayer | null
  visualizer: AudioVisualizer | null
  elements: {
    channelName: HTMLElement | null
    trackInfo: HTMLElement | null
    channelGrid: HTMLElement | null
    channelSearch: HTMLInputElement | null
    volumeSlider: HTMLInputElement | null
    volumeValue: HTMLElement | null
    muteBtn: HTMLButtonElement | null
    muteIcon: HTMLElement | null
    powerLed: HTMLElement | null
    statusIndicator: HTMLElement | null
    statusText: HTMLElement | null
    audioPlayer: HTMLAudioElement | null
    visualizer: HTMLCanvasElement | null
  }
}

const App: AppState = {
  channels: [],
  filteredChannels: [],
  searchQuery: '',
  channelsRendered: false,
  currentChannel: null,
  currentChannelId: null,
  isPlaying: false,
  isLoading: false,
  apiClient: null,
  radioPlayer: null,
  visualizer: null,
  elements: {
    channelName: null,
    trackInfo: null,
    channelGrid: null,
    channelSearch: null,
    volumeSlider: null,
    volumeValue: null,
    muteBtn: null,
    muteIcon: null,
    powerLed: null,
    statusIndicator: null,
    statusText: null,
    audioPlayer: null,
    visualizer: null,
  },
}

// ==========================================
// DOM Element References
// ==========================================
function initElements(): void {
  App.elements = {
    channelName: document.getElementById('channelName'),
    trackInfo: document.getElementById('trackInfo'),
    channelGrid: document.getElementById('channelGrid'),
    channelSearch: document.getElementById('channelSearch') as HTMLInputElement,
    volumeSlider: document.getElementById('volumeSlider') as HTMLInputElement,
    volumeValue: document.getElementById('volumeValue'),
    muteBtn: document.getElementById('muteBtn') as HTMLButtonElement,
    muteIcon: document.querySelector('.mute-icon'),
    powerLed: document.getElementById('powerLed'),
    statusIndicator: document.getElementById('statusIndicator'),
    statusText: document.getElementById('statusText'),
    audioPlayer: document.getElementById('audioPlayer') as HTMLAudioElement,
    visualizer: document.getElementById('visualizer') as HTMLCanvasElement,
  }
}

// ==========================================
// Initialization
// ==========================================
function init(): void {
  console.log('[App] Initializing Quran Radio...')

  // Initialize DOM references
  initElements()

  // Initialize components
  initApiClient()
  initRadioPlayer()
  initVisualizer()
  initControls()
  initSearch()

  // Start polling for channels
  App.apiClient?.startPolling()

  // Update status
  updateStatus('connected', 'Ready')

  console.log('[App] Initialization complete')
}

// ==========================================
// API Client Setup
// ==========================================
function initApiClient(): void {
  App.apiClient = new ApiClient()

  // Channels received
  App.apiClient.onChannels = (channels) => {
    console.log('[App] Channels received:', channels)
    App.channels = channels
    // Update filtered channels if no search is active
    if (!App.searchQuery) {
      App.filteredChannels = [...channels]
    } else {
      // Re-apply filter with new channels
      filterChannels()
    }
    renderChannels()

    // Update now-playing info for current channel
    if (App.currentChannelId) {
      const currentChannel = channels.find((ch) => ch.id === App.currentChannelId)
      if (currentChannel) {
        App.currentChannel = currentChannel
        if (!App.isLoading) {
          updateTrackInfo(currentChannel.playing_surah)
        }
      }
    }

    updateStatus('connected', 'Ready')
    App.elements.powerLed?.classList.add('active')
  }

  // API error
  App.apiClient.onError = (error) => {
    console.error('[App] API error:', error)
    updateStatus('error', 'Connection Error')
    App.elements.powerLed?.classList.remove('active')
  }
}

// ==========================================
// Radio Player Setup
// ==========================================
function initRadioPlayer(): void {
  App.radioPlayer = new RadioPlayer('audioPlayer')

  App.radioPlayer.onPlay = () => {
    App.isPlaying = true
    App.visualizer?.start()
    // Restore track info when playback starts
    if (App.currentChannel) {
      updateTrackInfo(App.currentChannel.playing_surah)
    }
  }

  App.radioPlayer.onPause = () => {
    App.isPlaying = false
    App.visualizer?.stop()
  }

  App.radioPlayer.onLoading = () => {
    App.isLoading = true
    updateTrackInfo('Loading stream...')
  }

  App.radioPlayer.onLoaded = () => {
    App.isLoading = false
    // Restore track info when loaded
    if (App.currentChannel) {
      updateTrackInfo(App.currentChannel.playing_surah)
    }
  }

  App.radioPlayer.onError = (error) => {
    console.error('[App] Player error:', error)
    App.isLoading = false
    App.isPlaying = false
    showError(error.message || 'Playback error')
    App.visualizer?.stop()
  }
}

// ==========================================
// Visualizer Setup
// ==========================================
function initVisualizer(): void {
  App.visualizer = new AudioVisualizer('visualizer')
}

// ==========================================
// Controls Setup
// ==========================================
const VOLUME_STORAGE_KEY = 'quran-radio-volume'

function getStoredVolume(): number {
  const stored = localStorage.getItem(VOLUME_STORAGE_KEY)
  if (stored !== null) {
    const volume = parseInt(stored, 10)
    if (!isNaN(volume) && volume >= 0 && volume <= 100) {
      return volume
    }
  }
  return 50 // Default volume
}

function saveVolume(volume: number): void {
  localStorage.setItem(VOLUME_STORAGE_KEY, volume.toString())
}

function initControls(): void {
  // Load stored volume or use default
  const initialVolume = getStoredVolume()

  // Set initial volume on the slider
  if (App.elements.volumeSlider) {
    App.elements.volumeSlider.value = initialVolume.toString()
  }

  // Apply volume to player
  App.radioPlayer?.setVolume(initialVolume)

  // Update volume display
  if (App.elements.volumeValue) {
    App.elements.volumeValue.textContent = initialVolume.toString()
  }

  // Volume slider - handle both input and touch events
  const volumeSlider = App.elements.volumeSlider
  if (volumeSlider) {
    // Regular input event for desktop
    volumeSlider.addEventListener('input', (e) => {
      const target = e.target as HTMLInputElement
      const volume = parseInt(target.value, 10)
      App.radioPlayer?.setVolume(volume)
      if (App.elements.volumeValue) {
        App.elements.volumeValue.textContent = volume.toString()
      }

      // Update mute state if volume changes
      if (volume > 0 && App.radioPlayer?.isMuted) {
        updateMuteButton(false)
      }
    })

    // Touch event handling for mobile
    let isDragging = false
    let sliderRect: DOMRect | null = null

    volumeSlider.addEventListener('touchstart', () => {
      isDragging = true
      sliderRect = volumeSlider.getBoundingClientRect()
    }, { passive: true })

    volumeSlider.addEventListener('touchmove', (e) => {
      if (!isDragging || !sliderRect) return

      const touch = e.touches[0]
      const x = touch.clientX - sliderRect.left
      const width = sliderRect.width
      const percentage = Math.max(0, Math.min(1, x / width))
      const volume = Math.round(percentage * 100)

      // Update slider value
      volumeSlider.value = volume.toString()

      // Update volume and display
      App.radioPlayer?.setVolume(volume)
      if (App.elements.volumeValue) {
        App.elements.volumeValue.textContent = volume.toString()
      }

      // Update mute state if volume changes
      if (volume > 0 && App.radioPlayer?.isMuted) {
        updateMuteButton(false)
      }
    }, { passive: true })

    volumeSlider.addEventListener('touchend', () => {
      isDragging = false
      sliderRect = null
    }, { passive: true })

    // Save volume on change (after user finishes dragging)
    volumeSlider.addEventListener('change', (e) => {
      const target = e.target as HTMLInputElement
      const volume = parseInt(target.value, 10)
      // Save volume to localStorage
      saveVolume(volume)
    })
  }

  // Mute button
  App.elements.muteBtn?.addEventListener('click', () => {
    const isMuted = App.radioPlayer?.toggleMute() ?? false
    updateMuteButton(isMuted)

    // Update volume slider display
    if (App.elements.volumeValue) {
      if (isMuted) {
        App.elements.volumeValue.textContent = '0'
      } else {
        App.elements.volumeValue.textContent = App.radioPlayer?.getVolume().toString() ?? '50'
      }
    }
  })

  // Initialize volume display
  if (App.elements.volumeValue && App.radioPlayer) {
    App.elements.volumeValue.textContent = App.radioPlayer.getVolume().toString()
  }
}

// ==========================================
// Search Functionality
// ==========================================
function initSearch(): void {
  App.elements.channelSearch?.addEventListener('input', (e) => {
    const target = e.target as HTMLInputElement
    App.searchQuery = target.value.trim().toLowerCase()
    filterChannels()
    renderChannels()
  })
}

function filterChannels(): void {
  if (!App.searchQuery) {
    App.filteredChannels = [...App.channels]
    return
  }

  App.filteredChannels = App.channels.filter((channel) => {
    const nameMatch = channel.reciter.toLowerCase().includes(App.searchQuery)
    const arNameMatch = channel.reciter_ar?.includes(App.searchQuery)
    return nameMatch || arNameMatch
  })
}

// ==========================================
// Channel Management
// ==========================================
function renderChannels(): void {
  const grid = App.elements.channelGrid
  if (!grid) return

  grid.innerHTML = ''

  // Use filtered channels, or all channels if not filtered yet
  const channelsToShow = App.filteredChannels.length > 0 || App.searchQuery
    ? App.filteredChannels
    : App.channels

  if (!channelsToShow || channelsToShow.length === 0) {
    if (App.searchQuery && App.channels.length > 0) {
      grid.innerHTML = '<div class="loading-text">No matching channels</div>'
    } else {
      grid.innerHTML = '<div class="loading-text">No channels available</div>'
    }
    return
  }

  channelsToShow.forEach((channel) => {
    const btn = document.createElement('button')
    // Only apply animation on first render
    btn.className = App.channelsRendered ? 'channel-btn no-animation' : 'channel-btn'
    btn.textContent = channel.reciter
    btn.dataset.channelId = channel.id
    btn.title = `${channel.reciter} - ${channel.playing_surah}`

    // Mark active channel
    if (channel.id === App.currentChannelId) {
      btn.classList.add('active')
    }

    // Click handler
    btn.addEventListener('click', () => selectChannel(channel))

    grid.appendChild(btn)
  })

  // Mark channels as rendered after first render
  App.channelsRendered = true
}

function selectChannel(channel: Channel): void {
  console.log('[App] Selecting channel:', channel.id, channel.reciter)

  // Check if channel has HLS URL
  if (!channel.hls_url) {
    showError('No stream URL available for this channel')
    return
  }

  // Store current channel
  App.currentChannel = channel

  // Update UI immediately
  if (App.elements.channelName) {
    App.elements.channelName.textContent = channel.reciter
  }
  updateTrackInfo(channel.playing_surah)

  // Update active button
  document.querySelectorAll('.channel-btn').forEach((btn) => {
    const el = btn as HTMLElement
    el.classList.toggle('active', el.dataset.channelId === channel.id)
  })

  App.currentChannelId = channel.id

  // Connect visualizer to audio on first interaction
  if (App.visualizer && !App.visualizer.isConnected && App.elements.audioPlayer) {
    try {
      App.visualizer.connect(App.elements.audioPlayer)
    } catch (e) {
      console.warn('[App] Visualizer connection deferred:', e)
    }
  }

  // Play the channel
  playChannel(channel.id, channel.hls_url)
}

function playChannel(channelId: string, streamUrl: string): void {
  console.log('[App] Playing channel:', channelId, streamUrl)

  App.currentChannelId = channelId
  App.radioPlayer?.playStream(streamUrl, channelId)
}

// ==========================================
// UI Updates
// ==========================================
function updateStatus(state: StatusState, text?: string): void {
  const { statusIndicator, statusText } = App.elements

  statusIndicator?.classList.remove('connected', 'error')

  switch (state) {
    case 'connected':
      statusIndicator?.classList.add('connected')
      if (statusText) statusText.textContent = text || 'Ready'
      break
    case 'disconnected':
      if (statusText) statusText.textContent = text || 'Disconnected'
      break
    case 'error':
      statusIndicator?.classList.add('error')
      if (statusText) statusText.textContent = text || 'Error'
      break
  }
}

function updateTrackInfo(text: string): void {
  if (App.elements.trackInfo) {
    App.elements.trackInfo.textContent = text || 'No info available'
  }
}

function updateMuteButton(isMuted: boolean): void {
  App.elements.muteBtn?.classList.toggle('muted', isMuted)
  if (App.elements.muteIcon) {
    App.elements.muteIcon.textContent = isMuted ? '🔇' : '🔊'
  }
}

function showError(message: string): void {
  if (App.elements.trackInfo) {
    App.elements.trackInfo.textContent = `Error: ${message}`
  }
}

// ==========================================
// Start Application
// ==========================================
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init)
} else {
  init()
}

// Expose App for debugging
; (window as any).QuranRadioApp = App